import { useState, useCallback } from "react";

/**
 * Generic CRUD operations hook
 * Handles Create, Read, Update, Delete operations with loading and error states
 */
export const useCRUD = (api, organizationId) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Fetch all items - memoized to prevent infinite loops
  const fetchAll = useCallback(async () => {
    if (!organizationId) {
      setError("Please select an organization");
      setLoading(false);
      return;
    }

    if (loading) return; // Prevent duplicate fetch if already loading

    try {
      setLoading(true);
      const response = await api.getAll(organizationId);
      const rawData = response.data.data || [];
      // Deduplicate by ID to prevent duplicate key warnings
      const uniqueData = rawData.filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.id === item.id),
      );
      setData(uniqueData);
      setError(null);
    } catch (err) {
      console.error("Error fetching data:", err);
      if (err.response?.status === 404) {
        setData([]);
        setError(null);
      } else {
        setError("Failed to fetch data");
      }
    } finally {
      setLoading(false);
    }
  }, [api, organizationId]);

  // Create item - memoized to prevent infinite loops
  const create = useCallback(
    async (itemData, successMsg, overrideOrgId) => {
      try {
        setActionLoading(true);
        // Use overrideOrgId if provided, or itemData.organizationId if current org is 'all'
        const orgIdToUse =
          overrideOrgId ||
          (organizationId === "all" ? itemData.organizationId : organizationId);

        if (!orgIdToUse || orgIdToUse === "all") {
          throw new Error(
            "Specific organization ID is required for this operation",
          );
        }

        const response = await api.create(orgIdToUse, itemData);
        setData((prev) => [...prev, response.data.data]);
        setError(null);
        if (successMsg) {
          setSuccessMessage(successMsg);
        }
        return response.data.data;
      } catch (err) {
        console.error("Error creating item:", err);
        setError(
          err.response?.data?.error || err.message || "Failed to create item",
        );
        throw err;
      } finally {
        setActionLoading(false);
      }
    },
    [api, organizationId],
  );

  // Update item - memoized to prevent infinite loops
  const update = useCallback(
    async (itemId, itemData, successMsg, overrideOrgId) => {
      try {
        setActionLoading(true);
        // Use overrideOrgId if provided, or itemData.organizationId if current org is 'all'
        const orgIdToUse =
          overrideOrgId ||
          (organizationId === "all" ? itemData.organizationId : organizationId);

        if (!orgIdToUse || orgIdToUse === "all") {
          throw new Error(
            "Specific organization ID is required for this operation",
          );
        }

        const response = await api.update(orgIdToUse, itemId, itemData);
        setData((prev) =>
          prev.map((item) => (item.id === itemId ? response.data.data : item)),
        );
        setError(null);
        if (successMsg) {
          setSuccessMessage(successMsg);
        }
        return response.data.data;
      } catch (err) {
        console.error("Error updating item:", err);
        setError(
          err.response?.data?.error || err.message || "Failed to update item",
        );
        throw err;
      } finally {
        setActionLoading(false);
      }
    },
    [api, organizationId],
  );

  // Delete item - memoized to prevent infinite loops
  const remove = useCallback(
    async (itemId, successMsg, overrideOrgId) => {
      try {
        setActionLoading(true);
        // For delete, we must have an orgId. If 'all', we need an override.
        const orgIdToUse = overrideOrgId || organizationId;

        if (!orgIdToUse || orgIdToUse === "all") {
          throw new Error(
            "Specific organization ID is required for this operation",
          );
        }

        await api.delete(orgIdToUse, itemId);
        setData((prev) => prev.filter((item) => item.id !== itemId));
        setError(null);
        if (successMsg) {
          setSuccessMessage(successMsg);
        }
      } catch (err) {
        console.error("Error deleting item:", err);
        setError(
          err.response?.data?.error || err.message || "Failed to delete item",
        );
        throw err;
      } finally {
        setActionLoading(false);
      }
    },
    [api, organizationId],
  );

  return {
    data,
    setData,
    loading,
    actionLoading,
    error,
    setError,
    successMessage,
    setSuccessMessage,
    fetchAll,
    create,
    update,
    remove,
  };
};
